import { Link } from 'react-router';

import ArticleCardInteractionEvent from '~/events/ArticleCardInteractionEvent';
import useArticleCardImpression from '~/hooks/useArticleCardImpression';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { IMAGE_TYPES } from '~/legacy/utils';
import { appendTrailingSlash } from '~/utils/trailingSlash';

import GranularResponsiveImage from './GranularResponsiveImage';

const MODULE_TYPE = 'search-panel-articles';

type ArticleHit = {
  canonical_path?: string;
  images?: {
    featured?: { alt?: string; original_img_url?: string };
  };
  name: string;
  objectID: string;
  slug: string;
  tracking_id?: number | string;
};

type Props = {
  hit: ArticleHit;
  insightsFields?: Record<string, unknown>;
  onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  position: number;
};

const getPath = ({ canonical_path, slug }: ArticleHit) =>
  appendTrailingSlash(canonical_path ? canonical_path : `/news/${slug}/`);

const ArticleSearchResult = ({
  onClick,
  insightsFields = {},
  hit,
  position
}: Props) => {
  const { isBot } = useRootData();
  const user = useUser();
  const impressionRef = useArticleCardImpression<HTMLAnchorElement>({
    articleId: hit.tracking_id?.toString(),
    moduleType: MODULE_TYPE,
    position
  });

  const {
    name,
    images: {
      featured: { alt: featuredAlt, original_img_url } = {
        alt: '',
        original_img_url: ''
      }
    } = {},
    objectID
  } = hit;

  const image = original_img_url ? (
    <GranularResponsiveImage
      alt={featuredAlt || name}
      url={original_img_url}
      title={name}
      responsiveImageType={IMAGE_TYPES.SEARCH_RESULT}
      preserveRatio
    />
  ) : (
    ''
  );

  const link = {
    pathname: getPath(hit),
    state: { hit, insightsFields }
  };

  return (
    <Link
      onClick={(event) => {
        new ArticleCardInteractionEvent({
          articleId: hit.tracking_id?.toString(),
          gesture: 'click',
          guestId: user.guestId,
          isBot,
          moduleType: MODULE_TYPE,
          position
        }).send();

        if (onClick) onClick(event);
      }}
      to={link}
      className="search-panel__list-item"
      ref={impressionRef}
      rel="article"
    >
      <div className="search-panel__image">{image}</div>
      <section className="search-panel__description">
        <h5
          className="font-heading text-sm font-normal line-clamp-3"
          id={`aria-article-title--${objectID}`}
        >
          {name}
        </h5>
      </section>
    </Link>
  );
};

export default ArticleSearchResult;
